import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useCallback, useMemo, useState } from "react";

export interface SetSupportTicketSelectedInput {
  supportTicketId: SupportTicketId;
  rowIndex: number;
  checked: boolean;
  shiftKey: boolean;
}

export interface SupportTicketTableSelectionState {
  excludedSupportTicketIds: SupportTicketId[];
  selectedSupportTicketIds: SupportTicketId[];
  selectedSupportTickets: SupportTicketListItemResponse[];
  selectedSupportTicketCount: number;
  allMatchingSupportTicketsSelected: boolean;
  hasAnySelectedSupportTickets: boolean;
  isSupportTicketSelected: (id: SupportTicketId) => boolean;
  setSupportTicketSelected: (input: SetSupportTicketSelectedInput) => void;
  setAllSupportTicketsSelected: (checked: boolean) => void;
  selectAllMatchingSupportTickets: () => void;
  clearSelectedSupportTickets: () => void;
}

const EMPTY_SUPPORT_TICKET_IDS: SupportTicketId[] = [];

export function useSupportTicketTableSelection(
  conversations: readonly SupportTicketListItemResponse[],
  totalCount: number,
  selectionScopeKey: string,
): SupportTicketTableSelectionState {
  const [rawSelectedSupportTicketIds, setRawSelectedSupportTicketIds] =
    useState<SupportTicketId[]>([]);
  const [rawExcludedSupportTicketIds, setRawExcludedSupportTicketIds] =
    useState<SupportTicketId[]>([]);
  const [selectionScopeKeyForState, setSelectionScopeKeyForState] =
    useState(selectionScopeKey);
  const [
    allMatchingSupportTicketsSelected,
    setAllMatchingSupportTicketsSelected,
  ] = useState(false);
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState<
    number | null
  >(null);

  const selectionScopeIsCurrent =
    selectionScopeKeyForState === selectionScopeKey;
  const scopedRawSelectedSupportTicketIds = selectionScopeIsCurrent
    ? rawSelectedSupportTicketIds
    : EMPTY_SUPPORT_TICKET_IDS;
  const scopedExcludedSupportTicketIds = selectionScopeIsCurrent
    ? rawExcludedSupportTicketIds
    : EMPTY_SUPPORT_TICKET_IDS;
  const scopedAllMatchingSupportTicketsSelected =
    selectionScopeIsCurrent && allMatchingSupportTicketsSelected;
  const scopedSelectionAnchorIndex = selectionScopeIsCurrent
    ? selectionAnchorIndex
    : null;

  const visibleSupportTicketIdSet = useMemo(
    () => new Set(conversations.map((conversation) => conversation.id)),
    [conversations],
  );

  const excludedSupportTicketIdSet = useMemo(
    () => new Set(scopedExcludedSupportTicketIds),
    [scopedExcludedSupportTicketIds],
  );

  const selectedSupportTicketIds = useMemo(
    () =>
      scopedAllMatchingSupportTicketsSelected
        ? conversations
            .map((conversation) => conversation.id)
            .filter((id) => !excludedSupportTicketIdSet.has(id))
        : scopedRawSelectedSupportTicketIds.filter((id) =>
            visibleSupportTicketIdSet.has(id),
          ),
    [
      conversations,
      excludedSupportTicketIdSet,
      scopedAllMatchingSupportTicketsSelected,
      scopedRawSelectedSupportTicketIds,
      visibleSupportTicketIdSet,
    ],
  );

  const selectedSupportTicketIdSet = useMemo(
    () => new Set(selectedSupportTicketIds),
    [selectedSupportTicketIds],
  );

  const selectedSupportTickets = useMemo(
    () =>
      conversations.filter((conversation) =>
        selectedSupportTicketIdSet.has(conversation.id),
      ),
    [conversations, selectedSupportTicketIdSet],
  );

  const selectedSupportTicketCount = scopedAllMatchingSupportTicketsSelected
    ? Math.max(totalCount - scopedExcludedSupportTicketIds.length, 0)
    : selectedSupportTicketIds.length;
  const hasAnySelectedSupportTickets = selectedSupportTicketCount > 0;

  const isSupportTicketSelected = useCallback(
    (id: SupportTicketId) =>
      scopedAllMatchingSupportTicketsSelected
        ? !excludedSupportTicketIdSet.has(id)
        : selectedSupportTicketIdSet.has(id),
    [
      excludedSupportTicketIdSet,
      scopedAllMatchingSupportTicketsSelected,
      selectedSupportTicketIdSet,
    ],
  );

  const setSupportTicketSelected = useCallback(
    ({
      supportTicketId,
      rowIndex,
      checked,
      shiftKey,
    }: SetSupportTicketSelectedInput) => {
      setSelectionScopeKeyForState(selectionScopeKey);

      const affectedSupportTicketIds =
        shiftKey && scopedSelectionAnchorIndex !== null
          ? conversations
              .slice(
                Math.min(scopedSelectionAnchorIndex, rowIndex),
                Math.max(scopedSelectionAnchorIndex, rowIndex) + 1,
              )
              .map((conversation) => conversation.id)
          : [supportTicketId];

      if (scopedAllMatchingSupportTicketsSelected) {
        setRawExcludedSupportTicketIds((previous) => {
          const next = new Set(selectionScopeIsCurrent ? previous : []);
          for (const id of affectedSupportTicketIds) {
            if (checked) {
              next.delete(id);
            } else {
              next.add(id);
            }
          }
          return [...next];
        });
        setSelectionAnchorIndex(rowIndex);
        return;
      }

      setAllMatchingSupportTicketsSelected(false);
      setRawExcludedSupportTicketIds([]);
      setRawSelectedSupportTicketIds((previous) => {
        const next = new Set(selectionScopeIsCurrent ? previous : []);

        for (const id of affectedSupportTicketIds) {
          if (checked) {
            next.add(id);
          } else {
            next.delete(id);
          }
        }

        return [...next];
      });

      setSelectionAnchorIndex(rowIndex);
    },
    [
      conversations,
      scopedAllMatchingSupportTicketsSelected,
      scopedSelectionAnchorIndex,
      selectionScopeIsCurrent,
      selectionScopeKey,
    ],
  );

  const setAllSupportTicketsSelected = useCallback(
    (checked: boolean) => {
      setSelectionScopeKeyForState(selectionScopeKey);
      if (scopedAllMatchingSupportTicketsSelected) {
        setRawExcludedSupportTicketIds((previous) => {
          const next = new Set(selectionScopeIsCurrent ? previous : []);
          for (const conversation of conversations) {
            if (checked) {
              next.delete(conversation.id);
            } else {
              next.add(conversation.id);
            }
          }
          return [...next];
        });
        setSelectionAnchorIndex(null);
        return;
      }

      setAllMatchingSupportTicketsSelected(false);
      setRawExcludedSupportTicketIds([]);
      if (checked) {
        setRawSelectedSupportTicketIds(
          conversations.map((conversation) => conversation.id),
        );
      } else {
        setRawSelectedSupportTicketIds([]);
      }
      setSelectionAnchorIndex(null);
    },
    [
      conversations,
      scopedAllMatchingSupportTicketsSelected,
      selectionScopeIsCurrent,
      selectionScopeKey,
    ],
  );

  const selectAllMatchingSupportTickets = useCallback(() => {
    setSelectionScopeKeyForState(selectionScopeKey);
    setRawSelectedSupportTicketIds([]);
    setRawExcludedSupportTicketIds([]);
    setAllMatchingSupportTicketsSelected(true);
    setSelectionAnchorIndex(null);
  }, [selectionScopeKey]);

  const clearSelectedSupportTickets = useCallback(() => {
    setSelectionScopeKeyForState(selectionScopeKey);
    setRawSelectedSupportTicketIds([]);
    setRawExcludedSupportTicketIds([]);
    setAllMatchingSupportTicketsSelected(false);
    setSelectionAnchorIndex(null);
  }, [selectionScopeKey]);

  return {
    excludedSupportTicketIds: scopedExcludedSupportTicketIds,
    selectedSupportTicketIds,
    selectedSupportTickets,
    selectedSupportTicketCount,
    allMatchingSupportTicketsSelected: scopedAllMatchingSupportTicketsSelected,
    hasAnySelectedSupportTickets,
    isSupportTicketSelected,
    setSupportTicketSelected,
    setAllSupportTicketsSelected,
    selectAllMatchingSupportTickets,
    clearSelectedSupportTickets,
  };
}
